/**
 * Arithmetic & Operations game question generators.
 * Long multiplication/division, BODMAS, powers of 10, word problems.
 * Uses icons, symbols, visual layouts and number charts.
 */

const shuffle = (arr) => {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
};

const makeDistractors = (correctNum, count = 3) => {
    const correct = String(correctNum);
    const seen = new Set([correct]);
    const out = [];
    const num = typeof correctNum === "number" ? correctNum : parseFloat(correctNum) || parseInt(correctNum, 10);
    const offsets = [1, 2, 3, 5, 10, 20, 50, -1, -2, -5];
    while (out.length < count) {
        const off = offsets[Math.floor(Math.random() * offsets.length)];
        const val = Number.isInteger(num) ? num + off : parseFloat((num + off).toFixed(1));
        const d = String(val);
        if (d !== correct && !seen.has(d)) {
            seen.add(d);
            out.push(d);
        }
    }
    return out;
};

// ---- long-mult-div: Long multiplication & division ----
const generateLongMultQuestion = (difficulty = "normal") => {
    let a, b;
    if (difficulty === "easy") {
        a = Math.floor(Math.random() * 90) + 10;  // 10–99
        b = Math.floor(Math.random() * 9) + 2;   // 2–10
    } else if (difficulty === "hard") {
        a = Math.floor(Math.random() * 900) + 100;  // 100–999
        b = Math.floor(Math.random() * 90) + 10;    // 10–99
    } else {
        a = Math.floor(Math.random() * 90) + 10;
        b = Math.floor(Math.random() * 90) + 10;    // 10–99 × 10–99
    }
    const answer = String(a * b);

    return {
        mode: "long-mult",
        question: "Calculate: (Use the grid if it helps)",
        expression: `${a} × ${b}`,
        operands: [a, b],
        answer,
        options: shuffle([answer, ...makeDistractors(parseInt(answer, 10))]),
        type: "long-mult-div",
        visualType: "mult-grid",
    };
};

const generateLongDivQuestion = (difficulty = "normal") => {
    let divisor, quotient;
    if (difficulty === "easy") {
        divisor = Math.floor(Math.random() * 9) + 2;
        quotient = Math.floor(Math.random() * 20) + 5;
    } else if (difficulty === "hard") {
        divisor = Math.floor(Math.random() * 20) + 10;
        quotient = Math.floor(Math.random() * 50) + 10;
    } else {
        divisor = Math.floor(Math.random() * 12) + 2;
        quotient = Math.floor(Math.random() * 30) + 10;
    }
    const dividend = divisor * quotient;
    const answer = String(quotient);

    return {
        mode: "long-div",
        question: "Calculate: (bus stop / long division)",
        expression: `${dividend} ÷ ${divisor}`,
        dividend,
        divisor,
        answer,
        options: shuffle([answer, ...makeDistractors(quotient)]),
        type: "long-mult-div",
        visualType: "div-layout",
    };
};

// ---- bodmas: Order of operations (BODMAS/BIDMAS) ----
const generateBodmasQuestion = (difficulty = "normal") => {
    let expr, answer;
    const templates = {
        easy: [
            { expr: (a, b) => `${a} + ${b} × 2`, ans: (a, b) => a + b * 2 },
            { expr: (a, b) => `${a} × 3 + ${b}`, ans: (a, b) => a * 3 + b },
            { expr: (a) => `(5 + ${a}) × 2`, ans: (a) => (5 + a) * 2 },
            { expr: (a) => `20 − ${a} × 2`, ans: (a) => 20 - a * 2 },
        ],
        normal: [
            { expr: (a, b) => `${a} + ${b} × 4`, ans: (a, b) => a + b * 4 },
            { expr: (a, b) => `(${a} + ${b}) × 2`, ans: (a, b) => (a + b) * 2 },
            { expr: (a, b) => `${a} × 3 − ${b}`, ans: (a, b) => a * 3 - b },
            { expr: (a) => `48 ÷ (6 + ${a})`, ans: (a) => Math.floor(48 / (6 + a)) },
        ],
        hard: [
            { expr: (a, b) => `${a} × 2 + ${b} × 3`, ans: (a, b) => a * 2 + b * 3 },
            { expr: (a, b) => `(${a} + ${b}) × (${a} − 1)`, ans: (a, b) => (a + b) * (a - 1) },
            { expr: (a) => `100 − ${a} × 4 ÷ 2`, ans: (a) => 100 - a * 4 / 2 },
        ],
    };
    const pool = templates[difficulty] || templates.normal;
    const t = pool[Math.floor(Math.random() * pool.length)];
    const a = Math.floor(Math.random() * 5) + 2;
    const b = Math.floor(Math.random() * 5) + 2;
    if (t.ans.length === 1) {
        expr = t.expr(a);
        answer = String(Math.round(t.ans(a)));
    } else {
        expr = t.expr(a, b);
        answer = String(Math.round(t.ans(a, b)));
    }

    return {
        mode: "bodmas",
        question: "Work out the answer. Remember: brackets, × ÷, then + −",
        expression: expr,
        bodmasOrder: ["( )", "× ÷", "+ −"],
        answer,
        options: shuffle([answer, ...makeDistractors(parseInt(answer, 10))]),
        type: "bodmas",
        visualType: "bodmas-chart",
    };
};

// ---- powers-of-10: Multiply/divide by 10, 100, 1000 ----
const generatePowersOf10Question = (difficulty = "normal") => {
    const powers = [10, 100, 1000];
    if (difficulty === "hard") powers.push(10000);
    const power = powers[Math.floor(Math.random() * powers.length)];
    const isMultiply = Math.random() < 0.5;

    let n;
    if (isMultiply) {
        if (difficulty === "easy") n = Math.floor(Math.random() * 90) + 10;
        else if (difficulty === "hard") n = Math.floor(Math.random() * 900) + 100;
        else n = Math.floor(Math.random() * 90) + 10;
    } else {
        // Divide: ensure whole-number result (n = power × k)
        const k = difficulty === "easy" ? Math.floor(Math.random() * 9) + 1
            : difficulty === "hard" ? Math.floor(Math.random() * 90) + 10
            : Math.floor(Math.random() * 20) + 5;
        n = power * k;
    }

    const answer = isMultiply ? String(n * power) : String(n / power);
    const symbol = isMultiply ? "×" : "÷";

    return {
        mode: "powers-of-10",
        question: "What is the result?",
        expression: `${n} ${symbol} ${power}`,
        number: n,
        power,
        operation: isMultiply ? "multiply" : "divide",
        answer,
        options: shuffle([answer, ...makeDistractors(parseFloat(answer))]),
        type: "powers-of-10",
        visualType: "place-shift",
    };
};

// ---- word-problems: Applying add, sub, mul, div in context ----
const generateArithmeticWordProblem = (difficulty = "normal") => {
    const range = difficulty === "easy" ? [5, 30] : difficulty === "hard" ? [20, 100] : [10, 50];
    const roll = () => Math.floor(Math.random() * (range[1] - range[0] + 1)) + range[0];

    const templates = [
        {
            q: (a, b) => `A shop has ${a} apples and ${b} oranges. How many fruits altogether?`,
            ans: (a, b) => a + b,
        },
        {
            q: (a, b) => `Sarah has £${a}. She buys a book for £${b}. How much is left?`,
            ans: (a, b) => a - b,
            constraint: (a, b) => a > b,
        },
        {
            q: (a, b) => `There are ${a} packs with ${b} sweets each. How many sweets in total?`,
            ans: (a, b) => a * b,
        },
        {
            q: (a, b) => `${a} marbles are shared equally among ${b} children. How many does each get?`,
            ans: (a, b) => Math.floor(a / b),
            gen: (roll, diff) => {
                const b = diff === "easy" ? Math.floor(Math.random() * 5) + 2 : Math.floor(Math.random() * 10) + 2;
                const k = Math.floor(Math.random() * 15) + 2;
                return [b * k, b];
            },
        },
        {
            q: (a, b) => `Tom buys ${a} pencils at ${b}p each. How much does he pay in pence?`,
            ans: (a, b) => a * b,
        },
        {
            q: (a, b) => `A bus has ${a} passengers. ${b} get off. How many remain?`,
            ans: (a, b) => a - b,
            constraint: (a, b) => a > b,
        },
    ];

    let t; let a; let b;
    for (let attempts = 0; attempts < 30; attempts++) {
        t = templates[Math.floor(Math.random() * templates.length)];
        if (t.gen) {
            [a, b] = t.gen(roll, difficulty);
        } else {
            a = roll();
            b = difficulty === "easy" ? Math.floor(Math.random() * 10) + 2 : roll();
            if (t.constraint && !t.constraint(a, b)) continue;
        }
        if (t.ans(a, b) >= 0) break;  // avoid negative answers
    }
    const finalAnswer = String(t.ans(a, b));
    const question = t.q(a, b);

    return {
        mode: "word-problem",
        question,
        answer: finalAnswer,
        options: shuffle([finalAnswer, ...makeDistractors(parseInt(finalAnswer, 10))]),
        type: "word-problems",
        visualType: "word-problem",
    };
};

// ---- Export: generate by game key ----
export const generateArithmeticOperationsQuestionByKey = (gameKey, difficulty = "normal") => {
    switch (gameKey) {
        case "long-mult-div":
            return Math.random() < 0.5
                ? generateLongMultQuestion(difficulty)
                : generateLongDivQuestion(difficulty);
        case "bodmas":
            return generateBodmasQuestion(difficulty);
        case "powers-of-10":
            return generatePowersOf10Question(difficulty);
        case "word-problems":
            return generateArithmeticWordProblem(difficulty);
        default:
            return generateLongMultQuestion(difficulty);
    }
};
